import React, { useMemo } from 'react';
import Router from 'next/router';
import { Box, Flex } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';

import Button from '@/components/Button';
import Carousel from '@/components/Carousel';
import Link from '@/components/Link';
import PackageHeroSummary from '@/components/PackageHeroSummary';
import PricePerTraveller from '@/components/PricePerTraveller';
import PropertyTitle from '@/components/PropertyTitle';
import Skeleton from '@/components/Skeleton';
import useBreakpoints, { isScreen } from '@/hooks/useBreakpoints';
import PropertyContent from '@/propTypes/PropertyContent';
import getPropertyLink from '@/utils/getPropertyLink';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import useSelectItemEvent from '@/v2/hooks/useSelectItemEvent';
import { dataLayer } from '@/v2/utils/dataLayer';
import useGetPackagesCheapestOffer from '../../../../../../hooks/useGetPackagesCheapestOffer';
import getOfferByPropertyId from '../../../../../../utils/getOfferByPropertyId';

const PropertyOption = React.memo(({ propertyContent }) => {
  const breakpoints = useBreakpoints();
  const { searchQuery, loading, packagesWithCheapestOffer } =
    useGetPackagesCheapestOffer();
  const shouldUseNewEvents = useGA4Events();

  const byPropertyId = getOfferByPropertyId(packagesWithCheapestOffer);
  const packageOffer = byPropertyId[propertyContent.id];

  const smallScreen = isScreen(breakpoints)('xs', 'sm');

  const href = getPropertyLink({
    propertyContent,
    packageOffer,
    searchQuery,
  });

  const destination = propertyContent?.destination?.title;

  const selectItemEventData = useSelectItemEvent({
    searchType: 'map',
    property: { propertyContent },
    destination,
    name: `${destination} Holidays`,
    price: packageOffer?.charges?.total?.amount,
    index: 1,
  });

  const onClick = () => {
    if (shouldUseNewEvents) {
      dataLayer.selectItemEvent(selectItemEventData);
      dataLayer.ctaClickEvent({
        itemText: 'Check availability',
        url: href?.pathname,
      });
    }
  };

  const images = useMemo(
    () =>
      propertyContent.images
        .slice(0, smallScreen ? 1 : Number.POSITIVE_INFINITY)
        .map((image) => ({
          caption: image.caption,
          src: image.large,
        })),
    [smallScreen, propertyContent],
  );

  const renderImages = () => (
    <Skeleton visible={loading} height="100%" display="block" borderRadius="0">
      {() => (
        <Carousel
          images={images}
          onClick={() => Router.push(href).then(() => window.scroll(0, 0))}
        />
      )}
    </Skeleton>
  );

  const renderInformation = () => (
    <Skeleton visible={loading} width="120px" lineHeight="1.5">
      <Link.Naked href={href}>
        <PropertyTitle
          propertyContent={propertyContent}
          withTags={smallScreen}
        />
      </Link.Naked>
    </Skeleton>
  );

  const renderCallToAction = () => (
    <>
      <Box>
        <Skeleton visible={loading} width="30px">
          <PackageHeroSummary searchQuery={searchQuery} />
        </Skeleton>
      </Box>
      <Box textAlign={[null, null, 'right']} mt="2">
        <Skeleton visible={loading} lineHeight="1.5" width="100px">
          {packageOffer && (
            <PricePerTraveller
              travellers={searchQuery}
              total={packageOffer.charges.total}
              perTraveller={packageOffer.charges.perTraveller}
            />
          )}
        </Skeleton>
      </Box>
      <Box mt="3">
        <Skeleton
          visible={loading}
          height={['31px', null, '49px']}
          width={['100%', null, '180px']}
        >
          {searchQuery && !packageOffer ? (
            <Button outline disabled>
              Not available
            </Button>
          ) : (
            <Link.Button onClick={onClick} href={href} outline>
              Find out more
            </Link.Button>
          )}
        </Skeleton>
      </Box>
    </>
  );

  return (
    <Box position="absolute" bottom="0" left="0" right="0" p="4">
      <Flex
        position="relative"
        zIndex="foreground"
        maxWidth="1040px"
        mx="auto"
        bg="white"
        borderRadius="sm"
        boxShadow="soft"
      >
        <Box
          height={[null, null, 'auto']}
          minHeight={['230px', null, '200px']}
          width={['130px', null, '340px']}
        >
          {renderImages()}
        </Box>
        <Flex flexDirection={['column', null, 'row']} flex="1">
          <Box flex="1" py={['3', null, '4']} px={['2', null, '6']}>
            {renderInformation()}
          </Box>
          <Flex
            flexDirection="column"
            alignItems="flex-end"
            justifyContent="flex-end"
            py={['3', null, '4']}
            px={['2', null, '6']}
          >
            {renderCallToAction()}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
});

PropertyOption.propTypes = {
  propertyContent: PropTypes.shape(PropertyContent).isRequired,
};

export default PropertyOption;
